import { Building2, ChevronLeft, ChevronRight, Edit, Eye, Trash2 } from "lucide-react";
import React, { useMemo, useState } from "react";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "../../../components/ui/alert-dialog";
import { Button } from "../../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";

export function CompaniesTable({ companies, onView, onEdit, onDelete, isLoading, itemsPerPage = 25 }) {
	const [currentPage, setCurrentPage] = useState(1);

	// Calculate pagination values
	const totalItems = companies.length;
	const totalPages = Math.ceil(totalItems / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;

	// Get current page items using useMemo for performance
	const currentPageCompanies = useMemo(() => {
		return companies.slice(startIndex, endIndex);
	}, [companies, startIndex, endIndex]);

	// Reset to first page when companies change
	React.useEffect(() => {
		setCurrentPage(1);
	}, [companies]);

	const goToPage = (page) => {
		setCurrentPage(Math.max(1, Math.min(page, totalPages)));
	};

	const goToPrevious = () => {
		goToPage(currentPage - 1);
	};

	const goToNext = () => {
		goToPage(currentPage + 1);
	};
	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="flex items-center space-x-2">
					<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
					<span>Loading companies...</span>
				</div>
			</div>
		);
	}

	if (companies.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-center">
				<Building2 className="h-12 w-12 text-muted-foreground mb-4" />
				<h3 className="text-lg font-semibold mb-2">No companies found</h3>
				<p className="text-muted-foreground">Get started by creating your first company.</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="rounded-md border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead colSpan={2}>Company</TableHead>
							<TableHead>Industry</TableHead>
							<TableHead>Size</TableHead>
							<TableHead>Headquarters</TableHead>
							<TableHead>Founded</TableHead>
							<TableHead>Website</TableHead>
							<TableHead className="text-right">Actions</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{currentPageCompanies.map((company) => (
							<TableRow key={company._id}>
								<TableCell colSpan={2}>
									<div className="flex items-center space-x-3">
										{company.logo_url ? (
											<img
												src={company.logo_url}
												alt={`${company.company_name} logo`}
												className="h-8 w-8 rounded object-cover"
											/>
										) : (
											<div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
												<Building2 className="h-4 w-4" />
											</div>
										)}
										<div>
											<div className="font-medium">{company.company_name}</div>
											<div className="text-sm text-muted-foreground">{company._id}</div>
										</div>
									</div>
								</TableCell>
								<TableCell>{company.company_industry || "N/A"}</TableCell>
								<TableCell>{company.company_size_approx || "N/A"}</TableCell>
								<TableCell>{company.headquarters || "N/A"}</TableCell>
								<TableCell>{company.founded || "N/A"}</TableCell>
								<TableCell>
									{company.company_website ? (
										<a
											href={company.company_website}
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary-700 font-medium hover:underline"
										>
											{company.company_website.replace(/^https?:\/\//, "")}
										</a>
									) : (
										"N/A"
									)}
								</TableCell>
								<TableCell className="text-right">
									<div className="flex items-center justify-end space-x-2">
										<Button variant="ghost" size="sm" onClick={() => onView(company)}>
											<Eye className="h-4 w-4" />
										</Button>
										<Button variant="ghost" size="sm" onClick={() => onEdit(company)}>
											<Edit className="h-4 w-4" />
										</Button>
										<AlertDialog>
											<AlertDialogTrigger asChild>
												<Button
													variant="ghost"
													size="sm"
													className="text-destructive hover:text-destructive hover:bg-destructive/10"
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</AlertDialogTrigger>
											<AlertDialogContent>
												<AlertDialogHeader>
													<AlertDialogTitle>Delete Company</AlertDialogTitle>
													<AlertDialogDescription>
														Are you sure you want to delete <strong>{company.company_name}</strong>? This action cannot
														be undone and will permanently remove the company from your database.
													</AlertDialogDescription>
												</AlertDialogHeader>
												<AlertDialogFooter>
													<AlertDialogCancel>Cancel</AlertDialogCancel>
													<AlertDialogAction
														onClick={() => onDelete(company)}
														className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
													>
														Delete Company
													</AlertDialogAction>
												</AlertDialogFooter>
											</AlertDialogContent>
										</AlertDialog>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			{/* Pagination Controls */}
			{totalPages > 1 && (
				<div className="flex items-center justify-between">
					<div className="text-sm text-muted-foreground">
						Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} companies
					</div>

					<div className="flex items-center space-x-2">
						<Button variant="outline" size="sm" onClick={goToPrevious} disabled={currentPage === 1}>
							<ChevronLeft className="h-4 w-4 mr-1" />
							Previous
						</Button>

						<div className="flex items-center space-x-1">
							{/* Show page numbers */}
							{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
								let pageNum;
								if (totalPages <= 5) {
									pageNum = i + 1;
								} else if (currentPage <= 3) {
									pageNum = i + 1;
								} else if (currentPage >= totalPages - 2) {
									pageNum = totalPages - 4 + i;
								} else {
									pageNum = currentPage - 2 + i;
								}

								return (
									<Button
										key={pageNum}
										variant={currentPage === pageNum ? "default" : "outline"}
										size="sm"
										onClick={() => goToPage(pageNum)}
										className="w-8 h-8 p-0"
									>
										{pageNum}
									</Button>
								);
							})}
						</div>

						<Button variant="outline" size="sm" onClick={goToNext} disabled={currentPage === totalPages}>
							Next
							<ChevronRight className="h-4 w-4 ml-1" />
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
